import imageCompression from 'browser-image-compression';
import localforage from 'localforage';

// API URL for photo uploads
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compress an image file before upload
 * Significantly reduces file size while maintaining quality
 */
export const compressImage = async (file, options = {}) => {
  const defaultOptions = {
    maxSizeMB: 0.5,          // Max file size in MB
    maxWidthOrHeight: 1920,   // Max dimension
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8
  };
  
  const compressionOptions = { ...defaultOptions, ...options };
  
  try {
    const compressedFile = await imageCompression(file, compressionOptions);
    console.log(`Compressed from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
    return compressedFile;
  } catch (error) {
    console.error('Image compression failed:', error);
    return file; // Return original if compression fails
  }
};

/**
 * Convert file to base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Compress and convert image to base64
 */
export const compressAndConvertToBase64 = async (file) => {
  const compressed = await compressImage(file);
  const base64 = await fileToBase64(compressed);
  return base64;
};

// ==================== IMMEDIATE PHOTO UPLOAD ====================

/**
 * Upload a photo immediately to the server
 * Returns photo_id and URL for reference
 */
export const uploadPhotoImmediate = async (file, options = {}) => {
  const {
    photoType = 'general',
    inspectionId = null,
    draftId = null,
    token,
    onProgress
  } = options;

  try {
    // Compress before upload
    const compressedFile = await compressImage(file);
    
    const formData = new FormData();
    formData.append('file', compressedFile);
    
    // Build URL with query params
    let url = `${API_URL}/api/photos/upload?photo_type=${photoType}`;
    if (inspectionId) url += `&inspection_id=${inspectionId}`;
    if (draftId) url += `&draft_id=${draftId}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }
    
    const result = await response.json();
    
    // Also get the base64 for immediate display
    const base64 = await fileToBase64(compressedFile);
    
    return {
      ...result,
      base64,  // For immediate display in UI
      uploaded: true
    };
  } catch (error) {
    console.error('Photo upload failed:', error);
    throw error;
  }
};

/**
 * Upload a base64 image directly to the server
 */
export const uploadBase64Photo = async (base64Data, options = {}) => {
  const {
    photoType = 'general',
    inspectionId = null,
    draftId = null,
    token
  } = options;

  try {
    // Convert base64 to blob
    const response = await fetch(base64Data);
    const blob = await response.blob();
    const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    
    return await uploadPhotoImmediate(file, {
      photoType,
      inspectionId,
      draftId,
      token
    });
  } catch (error) {
    console.error('Base64 photo upload failed:', error);
    throw error;
  }
};

/**
 * Get photo data from server by photo_id
 */
export const getPhotoFromServer = async (photoId, token) => {
  try {
    const response = await fetch(`${API_URL}/api/photos/${photoId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch photo');
    }
    
    const data = await response.json();
    return data.data; // Returns base64 data URL
  } catch (error) {
    console.error('Failed to get photo:', error);
    return null;
  }
};

/**
 * Migrate draft photos to created inspection
 */
export const migrateDraftPhotos = async (draftId, inspectionId, token) => {
  try {
    const response = await fetch(`${API_URL}/api/photos/migrate-draft?draft_id=${draftId}&inspection_id=${inspectionId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to migrate photos');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Photo migration failed:', error);
    throw error;
  }
};

// ==================== STORAGE MONITORING ====================

/**
 * Estimate browser storage usage
 */
export const getStorageEstimate = async () => {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      used: estimate.usage || 0,
      quota: estimate.quota || 0,
      percentUsed: estimate.quota ? Math.round((estimate.usage / estimate.quota) * 100) : 0
    };
  }
  
  // Fallback for browsers without StorageManager API
  return {
    used: 0,
    quota: 50 * 1024 * 1024, // Assume 50MB default
    percentUsed: 0
  };
};

/**
 * Check if storage is getting full (> threshold)
 */
export const isStorageNearFull = async (threshold = 70) => {
  const estimate = await getStorageEstimate();
  return estimate.percentUsed >= threshold;
};

/**
 * Get server-side storage status
 */
export const getServerStorageStatus = async (token) => {
  try {
    const response = await fetch(`${API_URL}/api/storage/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get storage status');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to get server storage status:', error);
    return null;
  }
};

/**
 * Get current GPS location
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        });
      },
      (error) => {
        console.error('Geolocation error:', error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  });
};

/**
 * Format GPS coordinates for display
 */
export const formatCoordinates = (lat, lng) => {
  if (!lat || !lng) return 'Location not captured';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

/**
 * Get address from coordinates using reverse geocoding (free service)
 */
export const getAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await response.json();
    return data.display_name || 'Address not found';
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
    return null;
  }
};

/**
 * Voice recording utility
 */
export class VoiceRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
  }
  
  async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);
      this.audioChunks = [];
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.start();
      this.isRecording = true;
      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      return false;
    }
  }
  
  stopRecording() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder) {
        resolve(null);
        return;
      }
      
      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.isRecording = false;
        
        // Convert to base64
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
          resolve({
            blob: audioBlob,
            base64: reader.result,
            duration: this.audioChunks.length
          });
        };
      };
      
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
    });
  }
}

/**
 * Speech to text utility
 */
export class SpeechToText {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.transcript = '';
  }
  
  isSupported() {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }
  
  start(onResult, onEnd) {
    if (!this.isSupported()) {
      console.error('Speech recognition not supported');
      return false;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-IN'; // English (India)
    
    this.recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      if (finalTranscript) {
        this.transcript += finalTranscript + ' ';
      }
      
      onResult && onResult(this.transcript, interimTranscript);
    };
    
    this.recognition.onend = () => {
      this.isListening = false;
      onEnd && onEnd(this.transcript);
    };
    
    this.recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      this.isListening = false;
    };
    
    this.transcript = '';
    this.recognition.start();
    this.isListening = true;
    return true;
  }
  
  stop() {
    if (this.recognition) {
      this.recognition.stop();
      this.isListening = false;
    }
    return this.transcript;
  }
}

/**
 * Offline storage utility using localForage
 */
// localforage is already imported at the top

// Configure localforage
localforage.config({
  name: 'MyPropMate',
  storeName: 'inspections'
});

export const offlineStorage = {
  // Save draft inspection locally
  saveDraft: async (inspectionData) => {
    const drafts = await localforage.getItem('offline_drafts') || [];
    const existingIndex = drafts.findIndex(d => d.localId === inspectionData.localId);
    
    if (existingIndex >= 0) {
      drafts[existingIndex] = { ...inspectionData, updatedAt: new Date().toISOString() };
    } else {
      drafts.push({
        ...inspectionData,
        localId: inspectionData.localId || `local_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        synced: false
      });
    }
    
    await localforage.setItem('offline_drafts', drafts);
    return drafts;
  },
  
  // Get all offline drafts
  getDrafts: async () => {
    return await localforage.getItem('offline_drafts') || [];
  },
  
  // Delete a draft
  deleteDraft: async (localId) => {
    const drafts = await localforage.getItem('offline_drafts') || [];
    const filtered = drafts.filter(d => d.localId !== localId);
    await localforage.setItem('offline_drafts', filtered);
    return filtered;
  },
  
  // Mark draft as synced
  markSynced: async (localId, serverId) => {
    const drafts = await localforage.getItem('offline_drafts') || [];
    const draft = drafts.find(d => d.localId === localId);
    if (draft) {
      draft.synced = true;
      draft.serverId = serverId;
      await localforage.setItem('offline_drafts', drafts);
    }
  },
  
  // Check if online
  isOnline: () => navigator.onLine,
  
  // Get pending sync count
  getPendingSyncCount: async () => {
    const drafts = await localforage.getItem('offline_drafts') || [];
    return drafts.filter(d => !d.synced).length;
  }
};

export default {
  compressImage,
  fileToBase64,
  compressAndConvertToBase64,
  uploadPhotoImmediate,
  uploadBase64Photo,
  getPhotoFromServer,
  migrateDraftPhotos,
  getStorageEstimate,
  isStorageNearFull,
  getServerStorageStatus,
  getCurrentLocation,
  formatCoordinates,
  getAddressFromCoordinates,
  VoiceRecorder,
  SpeechToText,
  offlineStorage
};
